import { useAccessPendingAction } from "./use-access-pending-action";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import { AccessMembershipPolicyInfo } from "./AccessMembershipPolicyInfo";
import { BadgeCheck, Globe, Plus, ShieldCheck } from "lucide-react";
import { Disclosure } from "@/components/design/Disclosure";
import { MenuItem } from "@/components/design/MenuItem";
import { groupLabel, groupExplanation, isSystemGroup, isAdministratorGroup, VERIFIED_GROUP_ID, REGISTERED_GROUP_ID } from "./access-group-labels";
import { ContentSharingPolicy } from "./ContentSharingPolicy";
import { GroupSharingEligibility } from "./GroupSharingEligibility";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { AdminMasterDetail } from "@/components/admin/AdminMasterDetail";
import { Button } from "@/components/design/Button";
import { TextInput, TextArea } from "@/components/design/TextField";
import { Tooltip } from "@/components/common/Tooltip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { AccessGroupDocument, AccessGroupsDocument, CreateAccessGroupDocument, UpdateAccessGroupDocument, DeleteAccessGroupDocument,
  type AccessGroupDetailsFragment } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { AccessGroupRolesPanel } from "./AccessGroupRolesPanel";
import { AccessGroupMembersPanel } from "./AccessGroupMembersPanel";
import { AccessAnonymousGroupPanel } from "./AccessAnonymousGroupPanel";
import { AnonymousAccessIcon } from "./AnonymousAccessIcon";

const ANONYMOUS_GROUP_ID = "anonymous";

export function AccessGroupsPanel() {
  const leaveGate = useUnsavedEditsGate();
  const deletion = useAccessPendingAction("access-group-delete");
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("groupAction") === "roles" ? "roles" : params.get("groupAction") === "members" ? "members" : "details");
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { data, loading, error, refetch, fetchMore } = useQuery(AccessGroupsDocument, { fetchPolicy: "network-only" });
  const [selected, setSelected] = useState<string | null>(params.get("groupId"));
  const [editing, setEditing] = useState<AccessGroupDetailsFragment | "new" | null>(params.get("groupAction") === "create" ? "new" : null);
  const [deleting, setDeleting] = useState<AccessGroupDetailsFragment | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [remove, { loading: removing }] = useMutation(DeleteAccessGroupDocument);
  const inFlight = useRef(false);
  const result = data?.accessGroups;
  const selectedId = selected ?? result?.groups[0]?.id;
  const anonymous = selectedId === ANONYMOUS_GROUP_ID;
  const detail = useQuery(AccessGroupDocument, {
    variables: { id: anonymous ? "" : selectedId ?? "" }, skip: !selectedId || anonymous, fetchPolicy: "cache-and-network",
  });
  const group = detail.data?.accessGroup && detail.data.accessGroup.id === selectedId ? detail.data.accessGroup
    : detail.loading ? result?.groups.find((item) => item.id === selectedId) : undefined;

  function selectGroup(id: string) {
    setSelected(id);
    setFailure(null);
    const next = new URLSearchParams(params);
    next.set("groupId", id);
    next.delete("groupAction");
    setParams(next, { replace: true, preventScrollReset: true });
  }

  async function loadMore() {
    if (loading || inFlight.current || !result?.nextAfter) return;
    inFlight.current = true;
    try {
      await fetchMore({ variables: { after: result.nextAfter }, updateQuery(previous, { fetchMoreResult }) {
        const page = fetchMoreResult.accessGroups;
        const groups = new Map(previous.accessGroups.groups.map((item) => [item.id, item]));
        page.groups.forEach((item) => groups.set(item.id, item));
        return { ...previous, accessGroups: { ...page, groups: [...groups.values()] } };
      } });
      setFailure(null);
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); }
    finally { inFlight.current = false; }
  }

  async function deleteGroup() {
    if (!deleting || inFlight.current) return;
    inFlight.current = true;
    try {
      await remove({ variables: { input: { id: deleting.id } } });
      setDeleting(null);
      setSelected(null);
      const next = new URLSearchParams(params);
      next.delete("groupId");
      next.delete("groupAction");
      setParams(next, { replace: true, preventScrollReset: true });
      await refetch();
      setFailure(null);
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); throw cause; }
    finally { inFlight.current = false; }
  }

  return <div className="space-y-4">
    {deletion.label && <p role="status" className="text-smycles-body font-medium">{deletion.label}</p>}
    {!anonymous && <ContentSharingPolicy />}
    {(error || (!anonymous && detail.error) || failure) && <p role="alert" className="text-smycles-body text-ink">{failure ?? userErrorMessage(intl, error ?? detail.error)}</p>}
    {(loading && !result) && <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>}
    <AdminMasterDetail title={t("admin.groups.title")} list={<div className="space-y-2">
      <Button id="groups-create" className="gap-2" disabled={!!editing} onClick={() => leaveGate.request(() => setEditing("new"))}><Plus className="size-4" aria-hidden="true" />{t("admin.groups.create")}</Button>
      {[false, true].map((system) => {
        const priority = (item: Parameters<typeof isAdministratorGroup>[0]) => isAdministratorGroup(item) ? 0
          : item.id === VERIFIED_GROUP_ID ? 1 : item.id === REGISTERED_GROUP_ID ? 2
          : item.id === ANONYMOUS_GROUP_ID ? 3 : 4;
        const groups = [...(result?.groups ?? []).filter((item) => isSystemGroup(item) === system),
          ...(system ? [{ id: ANONYMOUS_GROUP_ID, name: t("admin.groups.anonymous.title") }] : [])]
          .sort((a, b) => priority(a) - priority(b)
            || groupLabel(intl, a).localeCompare(groupLabel(intl, b), intl.locale));
        if (!groups.length && !system) return null;
        return <Disclosure key={String(system)} defaultOpen title={t(system ? "admin.groups.systemGroups" : "admin.groups.manualGroups")}>
          <div id={system ? "groups-system" : "groups-manual"}>{groups.map((item) => item.id === ANONYMOUS_GROUP_ID ? <MenuItem key={item.id} id="groups-item-anonymous"
            icon={<AnonymousAccessIcon className="size-4 shrink-0" />} active={!editing && anonymous}
            disabled={!!editing} onClick={() => leaveGate.request(() => selectGroup(ANONYMOUS_GROUP_ID))}>{t("admin.groups.anonymous.title")}</MenuItem>
            : <MenuItem key={item.id} id={`groups-item-${item.id}`}
            active={!editing && !anonymous && selectedId === item.id} disabled={!!editing}
            icon={item.id === VERIFIED_GROUP_ID ? <BadgeCheck className="size-4" aria-hidden="true" />
              : item.id === REGISTERED_GROUP_ID ? <Globe className="size-4" aria-hidden="true" />
              : isAdministratorGroup(item) ? <ShieldCheck className="size-4" aria-hidden="true" /> : undefined}
            onClick={() => leaveGate.request(() => selectGroup(item.id))}>
            <span id={`groups-item-${item.id}-name`} className="whitespace-normal break-words">{groupLabel(intl, item)}</span>
          </MenuItem>)}</div>
        </Disclosure>;
      })}
      {result?.hasNextPage && <Button variant="secondary" disabled={loading} onClick={() => void loadMore()}>{t("common.loadMore")}</Button>}
    </div>}>
      {anonymous && !editing ? <AccessAnonymousGroupPanel /> :
      <section className="box bg-surface p-6 space-y-6">
        {editing ? <AccessGroupEditor key={editing === "new" ? "new" : editing.id} group={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)} onSaved={async (id) => {
            setEditing(null); selectGroup(id);
            try {
              await refetch();
              if (id === selectedId) await detail.refetch();
            } catch (cause) { setFailure(userErrorMessage(intl, cause)); }
          }} /> : group ? <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div><h2 id="groups-detail-title" className="text-smycles-title font-medium">{groupLabel(intl, group)}</h2>
              {group.orgUnitId && <p className="mt-2 text-smycles-meta text-ink-muted">{t("admin.groups.department")}</p>}
</div>
            <div className="flex gap-2">
              {group.canUpdate && <Button variant="secondary" onClick={() => leaveGate.request(() => setEditing(group))}>{t("common.edit")}</Button>}
              {group.canDelete
                ? <Button id="groups-delete" variant="secondary" disabled={removing} onClick={() => leaveGate.request(() => setDeleting(group))}>{t("common.delete")}</Button>
                : <Tooltip id="groups-delete-hint" placement="bottom" content={t(group.orgUnitId ? "admin.groups.deleteBlocked.department"
                  : isSystemGroup(group) ? "admin.groups.deleteBlocked.system" : "admin.groups.deleteBlocked.references")}>
                  <Button id="groups-delete" variant="secondary" disabled className="pointer-events-none">{t("common.delete")}</Button>
                </Tooltip>}
            </div>
          </div>
          <SegmentedControl id="groups-sections" value={tab} onChange={(next) => leaveGate.request(() => setTab(next))} aria-label={t("admin.groups.sections")}
            options={[{ value: "details", label: t("admin.groups.details") }, { value: "members", label: t("admin.groups.members") },
              { value: "roles", label: t("admin.groups.rolesTab") }, { value: "sharing", label: t("admin.groups.sharingTab") }]} />
          {tab === "details" && <div className="space-y-4 text-smycles-body text-ink">
            <p>{groupExplanation(intl, group)}</p>
            {group.description && <p>{group.description}</p>}
            {(group.id === VERIFIED_GROUP_ID || group.id === REGISTERED_GROUP_ID) && <>
              {group.id === VERIFIED_GROUP_ID && <AccessMembershipPolicyInfo />}
              <p>{t("admin.groups.identityMembershipHint")}</p>
            </>}
          </div>}
          {tab === "sharing" && <GroupSharingEligibility key={group.id} groupId={group.id} />}
          {tab === "roles" && <AccessGroupRolesPanel key={`roles-${group.id}`} group={group} onChanged={async () => { await detail.refetch(); }} />}
          {tab === "members" && <AccessGroupMembersPanel key={group.id} group={group} onChanged={async () => { await detail.refetch(); }} />}
        </> : detail.loading ? <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>
          : !loading && <p className="text-smycles-body text-ink-muted">{t("admin.groups.empty")}</p>}
      </section>}
    </AdminMasterDetail>
    <UnsavedChangesDialog open={leaveGate.blocked} saving={leaveGate.saving} error={leaveGate.error}
      onStay={leaveGate.stay} onDiscard={leaveGate.discardAndProceed} onSave={leaveGate.saveAndProceed} />
    <ConfirmDialog id="groups-delete-dialog" open={!!deleting} title={t("admin.groups.deleteTitle")} message={deleting?.name}
      onCancel={() => { if (!removing) setDeleting(null); }} onConfirm={() => { if (deleting) { deletion.stage(`${t("common.delete")}: ${groupLabel(intl, deleting)}`, deleteGroup); setDeleting(null); } }} />
  </div>;
}

function AccessGroupEditor({ group, onSaved, onCancel }: {
  group: AccessGroupDetailsFragment | null; onSaved: (id: string) => Promise<void>; onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [create, { loading: creating }] = useMutation(CreateAccessGroupDocument);
  const [update, { loading: updating }] = useMutation(UpdateAccessGroupDocument);
  const savePromise = useRef<Promise<void> | null>(null);
  const saving = creating || updating;
  const dirty = name !== (group?.name ?? "") || description !== (group?.description ?? "");
  usePendingScope("access-group-editor", { dirty, save, discard: onCancel });
  function save(): Promise<void> {
    if (savePromise.current) return savePromise.current;
    const pending = persist().finally(() => { savePromise.current = null; });
    savePromise.current = pending;
    return pending;
  }
  async function persist() {
    if (!dirty) return;
    if (!name.trim()) throw new LocalizedError(t("admin.groups.nameRequired"));
    try {
      const input = { name: name.trim(), description: description.trim() || null };
      const id = group
        ? (await update({ variables: { input: { ...input, id: group.id } } })).data?.updateAccessGroup.id
        : (await create({ variables: { input } })).data?.createAccessGroup.id;
      if (id) await onSaved(id);
    } catch (cause) { const message = userErrorMessage(intl, cause); setError(message); throw new LocalizedError(message); }
  }
  return <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); }}>
    <h2 className="text-smycles-title font-medium">{t(group ? "admin.groups.edit" : "admin.groups.create")}</h2>
    <label className="block"><span className="smycles-field-label">{t("admin.groups.name")}</span>
      <TextInput id="groups-name" required maxLength={120} value={name} disabled={saving} onChange={(event) => setName(event.target.value)} /></label>
    <label className="block"><span className="smycles-field-label">{t("admin.roles.description")}</span>
      <TextArea maxLength={2000} value={description} disabled={saving} onChange={(event) => setDescription(event.target.value)} /></label>
    {error && <p role="alert" className="text-smycles-body text-ink">{error}</p>}
    <div className="flex gap-2"><Button variant="secondary" disabled={saving} onClick={onCancel}>{t("common.cancel")}</Button></div>
  </form>;
}
